import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import DateCalendar from './DateCalendar';
import { useTranslation } from 'react-i18next';

// Completed dates use ISO in form state; incomplete drafts remain visible for validation.
export default function DateField({ type, value, name, id, className, readOnly, disabled, required, min, max, onChange, onBlur, ...rest }) {
  const { t, i18n } = useTranslation();
  const english = i18n.resolvedLanguage === 'en';
  const [draft, setDraft] = useState(null);
  const editingFormat = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);
  const pickerValue = useRef(value || '');
  const [open, setOpen] = useState(false);
  const [blurValue, setBlurValue] = useState(undefined);
  const withTime = type === 'datetime-local';
  const isoFormat = withTime ? 'YYYY-MM-DDTHH:mm' : 'YYYY-MM-DD';
  const dateFormat = english ? 'DD/MM/YYYY' : 'DD.MM.YYYY';
  const displayFormat = withTime ? `${dateFormat} HH:mm` : dateFormat;
  const current = value || '';
  const parsed = moment(current, isoFormat, true);
  const valid = parsed.isValid();
  const displayValue = draft !== null ? draft : valid ? parsed.format(displayFormat) : current;
  const label = rest['aria-label'] || name || id || t('calendar:controls.date');
  const emit = (nextValue) => onChange({ target: { name, id, value: nextValue, type } });
  const closePicker = restoreFocus => {
    setOpen(false);
    setBlurValue(pickerValue.current);
    if (restoreFocus) triggerRef.current.focus();
  };

  useEffect(() => {
    if (blurValue !== undefined) {
      onBlur({ target: { name, id, value: blurValue, type } });
      setBlurValue(undefined);
    }
  }, [blurValue, onBlur, name, id, type]);

  useEffect(() => {
    let message = '';
    if (current && !valid) message = t(withTime ? 'calendar:invalidDateTime' : 'calendar:invalidDate');
    else if (current && ((min && current < min) || (max && current > max))) message = t('calendar:dateOutsideRange');
    inputRef.current.setCustomValidity(message);
  }, [current, valid, withTime, min, max, t]);

  return (
    <div className={`input-group date-field${className.includes('is-invalid') ? ' is-invalid' : ''}`}>
      <input
        {...rest}
        ref={inputRef}
        id={id}
        name={name}
        type="text"
        className={className}
        value={displayValue}
        placeholder={t(withTime ? 'calendar:dateTimePlaceholder' : 'calendar:datePlaceholder')}
        readOnly={readOnly}
        disabled={disabled}
        required={required}
        autoComplete="off"
        onFocus={() => { editingFormat.current = displayFormat; setDraft(displayValue); }}
        onChange={(event) => {
          if (editingFormat.current) setDraft(event.target.value);
          const next = moment(event.target.value, editingFormat.current || displayFormat, true);
          emit(next.isValid() ? next.format(isoFormat) : event.target.value);
        }}
        onBlur={() => { setDraft(null); editingFormat.current = null; onBlur({ target: { name, id, value: current, type } }); }}
      />
      {!readOnly && (
        <div className="input-group-append polish-date-picker">
          <button
            ref={triggerRef}
            type="button"
            className="input-group-text"
            aria-label={t('calendar:chooseDate', { label })}
            aria-haspopup="dialog"
            aria-expanded={open}
            disabled={disabled}
            onClick={() => {
              if (open) closePicker(false);
              else { pickerValue.current = current; setOpen(true); }
            }}
            onBlur={() => { if (!open) setBlurValue(current); }}
          >▦
          </button>
        </div>
      )}
      {open && !disabled && !readOnly && (
        <DateCalendar
          value={valid ? current : ''}
          withTime={withTime}
          min={min}
          max={max}
          anchor={triggerRef}
          label={label}
          onSelect={nextValue => {
            setDraft(null);
            pickerValue.current = nextValue;
            emit(nextValue);
          }}
          onClose={closePicker}
        />
      )}
    </div>
  );
}

DateField.propTypes = {
  type: PropTypes.oneOf(['date', 'datetime-local']),
  value: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  className: PropTypes.string,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  min: PropTypes.string,
  max: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};
DateField.defaultProps = {
  type: 'date',
  value: '',
  name: undefined,
  id: undefined,
  className: 'form-control',
  readOnly: false,
  disabled: false,
  required: false,
  min: undefined,
  max: undefined,
  onChange: () => {},
  onBlur: () => {},
};
